/**
 * @file
 * Actions shared between reducers need to be placed in separate files to avoid
 * cyclic dependencies, otherwise the createAsyncThunk functions won't resolve
 * before use and the actions will be undefined.
 */

import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import RepAI from '@repai/widget';
import {
  CreateAccountInfo,
  Credentials,
  Domain,
  ERROR_CODES,
  Org,
  OrgRole,
  OrgSettings,
  SelfAccount,
  SelfUser,
  SessionDetails,
  User,
  CannedResponse,
  UserIntegration,
  UserGroup,
  Segment,
  HelpCenterArticle,
  HelpCenterArticleStatus,
  HelpCenterCollection,
  JourneyVideo,
  HubspotSettings,
  SelfAccountOrPublicAccount,
  SelfPublicAccountWithToken,
} from 'types';
import type { AppState } from './reducers';
import { DashboardState } from './dashboard-slice';
import {
  authAccount as apiAuthAccount,
  ssoAuthAccount as ssoApiAuthAccount,
  createAccount as apiCreateAccount,
  createOrg as apiCreateOrg,
  updateOrgSettings as apiUpdateOrgSettings,
  updateHubspotSettings as apiUpdateHubspotSettings,
  checkAccountAuth,
  createOrgDomain,
  postInternalAuthImpersonate,
  removeOrgDomain,
  removeOrgMember,
  saveOrgClearbitIntegration,
  postOAuthOrgIntegration,
  putOrgIntegration,
  deleteOrgIntegration,
  postUserIntegration,
  putUserIntegration,
  deleteUserIntegration,
  updateTeamMember as apiUpdateTeamMember,
  putReplayPermission,
  createCannedResponse,
  deleteCannedResponse,
  updateCannedResponse,
  saveOrgChiliPiperIntegration,
  saveOrgApolloIntegration,
  saveOrgHubSpotMeetingsIntegration,
  putRequireSecureIdentities,
  createUserGroup,
  updateUserGroup,
  deleteUserGroup,
  addUserGroupUser,
  deleteUserGroupUser,
  postOrgIntegration,
  deleteSegment,
  createSegment,
  updateSegment,
  postImage,
  updateHelpCenterArticle,
  createHelpCenterArticle,
  deleteHelpCenterArticle,
  updateHelpCenterCollection,
  createHelpCenterCollection,
  deleteHelpCenterCollection,
  reassignHelpCenterCollection,
  putReorderAny,
  postAny,
  deleteAny,
  patchAny,
  postContactProperty,
  updateContactProperty,
  postCompanyProperty,
  updateCompanyProperty,
  postDealProperty,
  updateDealProperty,
  getUserPermissionsGroup,
  updateUserPermissionsGroup,
  saveOrgSixsenseIntegration,
  getUniqueVisitor,
  authSalesfloorWithLinkedIn,
  getOrgSettings,
} from 'shared/api';
import { RejectError } from 'shared/api/axios';
import {
  OrgIntegrationService,
  UserIntegrationService,
  Arguments,
  ImageRegistration,
  UniqueVisitor,
} from 'sb-lib/types';
import {
  makeBlockGeneralEventAgentCall,
  makeBlockGeneralEventVisitorCallbackAgent,
  makeBlockGeneralEventVisitorMissedAgentCall,
  makeChatMessageFromAgent,
} from 'sb-lib/chat-message-utils';
import { setStoredAuthenticationToken } from './storage';
import { setOutgoingVideoEnabled, startMediaStream } from 'shared/realtime';
import {
  selectActiveUser,
  selectActiveVisitor,
  selectAuthedAccount,
} from './shared-selectors';
import { selectFindAssociatedRealtimeVisitor } from './realtime-slice';
import { Property } from 'types/property';
import { PermissionGroup } from 'types/permissions';
import { sendChatMessage } from './chat-slice';
import { userToAgent } from 'shared/utils/user-agent';
import { makeLog } from 'shared/utils/log';
import { initStore } from 'shared/store/init';
import { store } from 'shared/store/store';

const log = makeLog('shared-actions');

export const checkAccount = createAsyncThunk<
  SelfAccountOrPublicAccount,
  void,
  { state: AppState; rejectValue: RejectError }
>(
  'auth/checkAccount',
  async () => {
    try {
      const res = await checkAccountAuth();
      return res.data;
    } catch (error) {
      if (error.code === ERROR_CODES.NO_ORG_AUTHORITY) {
        // heavy-handed, but this should be fairly rare
        document.location.reload();
      }
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      return !(state.auth.isAuthingUser || state.auth.isLoggingOut);
    },
  },
);

export const setAccount = createAction<SelfAccountOrPublicAccount>('auth/setAccount');
export const setNoAccount = createAction('auth/setNoAccount');

export const authAccount = createAsyncThunk<
  SelfAccount,
  Credentials,
  { rejectValue: RejectError }
>('auth/authAccount', async credentials => {
  const res = await apiAuthAccount(credentials);
  if (res.data.authenticationToken) {
    setStoredAuthenticationToken(res.data.authenticationToken);
  }
  return res.data;
});

export const ssoAuthAccount = createAsyncThunk<
  SelfAccount,
  { code: string },
  { rejectValue: RejectError }
>('auth/ssoAuthAccount', async ({ code }) => {
  const res = await ssoApiAuthAccount(code);
  if (res.data.authenticationToken) {
    setStoredAuthenticationToken(res.data.authenticationToken);
  }
  return res.data;
});

export const salesfloorLinkedInSignin = createAsyncThunk<
  SelfPublicAccountWithToken,
  { code: string; roomId: string },
  { state: AppState; rejectValue: RejectError }
>('auth/salesfloorLinkedInSignin', async ({ code, roomId }, { dispatch }) => {
  const res = await authSalesfloorWithLinkedIn(code, roomId);
  if (res.data.AccessToken) {
    setStoredAuthenticationToken(res.data.AccessToken);
    dispatch(checkAccount());
  }
  return res.data;
});

export const createAccount = createAsyncThunk<
  SelfAccount,
  CreateAccountInfo,
  { state: AppState; rejectValue: RejectError & { existing?: boolean } }
>('auth/createAccount', async (user, { rejectWithValue }) => {
  const res = await apiCreateAccount(user);

  if (res.data.existing) {
    return rejectWithValue({
      message: 'Confirm your account',
      existing: res.data.existing,
    });
  }

  if (res.data.authenticationToken) {
    setStoredAuthenticationToken(res.data.authenticationToken);
  }

  // ServiceBell alert for signup
  const { users, emailAddress } = res.data;
  const displayName = users[0]?.displayName || 'Unknown user';
  RepAI('alert', {
    title: 'Rep.ai signup',
    body: `${displayName} (${emailAddress}) has signed up for Rep.ai!`,
  });

  return res.data;
});

export const updateTeamMember = createAsyncThunk<
  User,
  { id: number; roles?: OrgRole[]; onCall?: boolean },
  { state: AppState; rejectValue: RejectError }
>('org/updateTeamMember', async ({ id, ...args }, { dispatch, getState }) => {
  const res = await apiUpdateTeamMember(id, args);
  // Re-fetch self if we just updated self
  const me = selectAuthedAccount(getState());
  const userIds = me?.users.map(u => u.id);
  if (userIds && userIds.includes(id)) {
    dispatch(checkAccount());
  }
  return res.data;
});

export const saveOrgIntegration = createAsyncThunk<
  Org,
  { code: string; service: OrgIntegrationService; extraData?: Record<string, string> },
  { state: AppState; rejectValue: RejectError }
>('org/saveOrgIntegration', async ({ code, service, extraData }) => {
  const res = await postOAuthOrgIntegration(code, service, extraData);
  return res.data;
});

export const saveClearbitIntegration = createAsyncThunk<
  Org,
  Parameters<typeof saveOrgClearbitIntegration>[0],
  { state: AppState; rejectValue: RejectError }
>('org/saveClearbitIntegration', async args => {
  const res = await saveOrgClearbitIntegration(args);
  return res.data;
});

export const saveSixsenseIntegration = createAsyncThunk<
  Org,
  Parameters<typeof saveOrgSixsenseIntegration>[0],
  { state: AppState; rejectValue: RejectError }
>('org/saveSixsenseIntegration', async args => {
  const res = await saveOrgSixsenseIntegration(args);
  return res.data;
});

export const saveChiliPiperIntegration = createAsyncThunk<
  Org,
  Parameters<typeof saveOrgChiliPiperIntegration>[0],
  { state: AppState; rejectValue: RejectError }
>('org/saveChiliPiperIntegration', async args => {
  const res = await saveOrgChiliPiperIntegration(args);
  return res.data;
});

export const saveApolloIntegration = createAsyncThunk<
  Org,
  Parameters<typeof saveOrgApolloIntegration>[0],
  { state: AppState; rejectValue: RejectError }
>('org/saveApolloIntegration', async args => {
  const res = await saveOrgApolloIntegration(args);
  return res.data;
});

export const saveHubSpotMeetingsIntegration = createAsyncThunk<
  Org,
  Parameters<typeof saveOrgHubSpotMeetingsIntegration>[0],
  { state: AppState; rejectValue: RejectError }
>('org/saveHubSpotMeetingsIntegration', async args => {
  const res = await saveOrgHubSpotMeetingsIntegration(args);
  return res.data;
});

export const addOrgIntegration = createAsyncThunk<
  Org,
  { service: OrgIntegrationService; data: object },
  { state: AppState; rejectValue: RejectError }
>('org/saveTeamsIntegration', async ({ service, data }) => {
  const res = await postOrgIntegration(service, data);
  return res.data;
});

export const removeOrgIntegration = createAsyncThunk<
  Org,
  OrgIntegrationService,
  { state: AppState; rejectValue: RejectError }
>('org/removeOrgIntegration', async service => {
  const res = await deleteOrgIntegration(service);
  return res.data;
});

export const updateOrgIntegration = createAsyncThunk<
  Org,
  { service: OrgIntegrationService; data: object },
  { state: AppState; rejectValue: RejectError }
>('org/updateOrgIntegration', async ({ service, data }) => {
  const res = await putOrgIntegration(service, data);
  return res.data;
});

export const saveUserIntegration = createAsyncThunk<
  UserIntegration[],
  { code: string; service: UserIntegrationService; extraData?: Record<string, string> },
  { state: AppState; rejectValue: RejectError }
>('org/saveUserIntegration', async ({ code, service }) => {
  const res = await postUserIntegration(code, service);
  return res.data;
});

export const removeUserIntegration = createAsyncThunk<
  UserIntegration[],
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeUserIntegration', async id => {
  const res = await deleteUserIntegration(id);
  return res.data;
});

export const updateUserIntegration = createAsyncThunk<
  UserIntegration[],
  { id: number; data: object },
  { state: AppState; rejectValue: RejectError }
>('org/updateUserIntegration', async ({ id, data }) => {
  const res = await putUserIntegration(id, data);
  return res.data;
});

export const addDomain = createAsyncThunk<
  Domain,
  string,
  { state: AppState; rejectValue: RejectError }
>('org/addDomain', async (url: string) => {
  return (await createOrgDomain(url)).data;
});

export const removeDomain = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeDomain', async (id: number) => {
  await removeOrgDomain(id);
});

export const addCannedResponse = createAsyncThunk<
  CannedResponse,
  { code: string; content: string },
  { state: AppState; rejectValue: RejectError }
>('org/addCannedResponse', async ({ code, content }) => {
  return (await createCannedResponse(code, content)).data;
});

export const editCannedResponse = createAsyncThunk<
  CannedResponse,
  Pick<CannedResponse, 'id' | 'code' | 'content'>,
  { state: AppState; rejectValue: RejectError }
>('org/editCannedResponse', async ({ id, code, content }) => {
  return (await updateCannedResponse({ id, code, content })).data;
});

export const removeCannedResponse = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeCannedResponse', async id => {
  await deleteCannedResponse(id);
});

export const addUserGroup = createAsyncThunk<
  UserGroup,
  { name: string },
  { state: AppState; rejectValue: RejectError }
>('org/addUserGroup', async ({ name }) => {
  return (await createUserGroup(name)).data;
});

export const addUserToGroup = createAsyncThunk<
  UserGroup,
  { userGroupId: number; userId: number },
  { state: AppState; rejectValue: RejectError }
>('org/addUserToGroup', async ({ userGroupId, userId }) => {
  return (await addUserGroupUser(userGroupId, userId)).data;
});

export const removeUserFromGroup = createAsyncThunk<
  UserGroup,
  { userGroupId: number; userId: number },
  { state: AppState; rejectValue: RejectError }
>('org/addUserToGroup', async ({ userGroupId, userId }) => {
  return (await deleteUserGroupUser(userGroupId, userId)).data;
});

export const editUserGroup = createAsyncThunk<
  UserGroup,
  Pick<UserGroup, 'id' | 'name'>,
  { state: AppState; rejectValue: RejectError }
>('org/editUserGroup', async ({ id, name }) => {
  return (await updateUserGroup({ id, name })).data;
});

export const removeUserGroup = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeUserGroup', async id => {
  await deleteUserGroup(id);
});

export const addSegment = createAsyncThunk<
  Segment,
  { name: string },
  { state: AppState; rejectValue: RejectError }
>('org/addSegment', async ({ name }) => {
  return (await createSegment(name)).data;
});

export const editSegment = createAsyncThunk<
  Segment,
  Partial<Segment>,
  { state: AppState; rejectValue: RejectError }
>('org/editSegment', async segment => {
  return (await updateSegment(segment)).data;
});

export const removeSegment = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeSegment', async id => {
  await deleteSegment(id);
});

export const addJourneyVideo = createAsyncThunk<
  JourneyVideo,
  { name: string; videoProxyId: string },
  { state: AppState; rejectValue: RejectError }
>('org/addJourneyVideo', async ({ name, videoProxyId }) => {
  return (await postAny<JourneyVideo>('journeys/journey_videos', { name, videoProxyId }))
    .data;
});

export const refreshOrgSettings = createAsyncThunk<Org, void>(
  'org/refreshSettings',
  async () => {
    return (await getOrgSettings()).data;
  },
);

export const updateOrgSettings = createAsyncThunk<
  Org,
  Partial<OrgSettings>,
  { state: AppState; rejectValue: RejectError }
>('org/updateSettings', async settings => {
  return (await apiUpdateOrgSettings(settings)).data;
});

export const updateHubspotSettings = createAsyncThunk<
  HubspotSettings,
  Partial<HubspotSettings>,
  { state: AppState; rejectValue: RejectError }
>('integrations/org/hubspot/settings', async settings => {
  return (await apiUpdateHubspotSettings(settings)).data;
});

export const updateRequireSecureIdentities = createAsyncThunk<Org, boolean>(
  'org/updateRequireSecureIdentities',
  async required => {
    return (await putRequireSecureIdentities(required)).data;
  },
);

export const shareOrgRecording = createAsyncThunk<
  SessionDetails,
  Arguments<typeof putReplayPermission>[0],
  { state: AppState; rejectValue: RejectError }
>('org/shareRecording', async args => {
  return (await putReplayPermission(args)).data;
});

export const removeMember = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeMember', async id => {
  await removeOrgMember(id);
});

export const createOrg = createAsyncThunk<
  SelfUser,
  Arguments<typeof apiCreateOrg>[0],
  { state: AppState; rejectValue: RejectError }
>('org/createOrg', async args => {
  return (await apiCreateOrg(args)).data;
});

export const changeActiveOrg = createAction<number>('org/changeActiveOrg');

export const authImpersonateAccount = createAsyncThunk<
  SelfAccount,
  string,
  { rejectValue: RejectError }
>('auth/authImpersonateAccount', async code => {
  const res = await postInternalAuthImpersonate(code);
  if (res.data.authenticationToken) {
    setStoredAuthenticationToken(res.data.authenticationToken);
    initStore(store);
  }
  return res.data;
});

export const receivedOrgAvailability = createAction<
  Pick<DashboardState, 'available' | 'availableAdminIds'>
>('dashboard/receivedOrgAvailability');
export const orgPlanUsageUpdated = createAction<
  Pick<
    DashboardState,
    | 'orgProactiveCallsRemaining'
    | 'orgReactiveCallsRemaining'
    | 'orgTakeoversRemaining'
    | 'orgSpyViewingsRemaining'
    | 'orgSixsenseCompanyDetailFetchesRemaining'
  >
>('dashboard/orgPlanUsageUpdated');

export const callWanted = createAsyncThunk<
  number,
  {
    callConversationId: number;
    intentionSource: 'visitor' | 'agent';
    reason?: 'declined' | 'missed';
    video?: boolean;
  },
  { state: AppState }
>(
  'realtime/callWanted',
  async (
    { callConversationId, intentionSource, reason, video = true },
    { getState, dispatch },
  ) => {
    const selfUser = selectActiveUser(getState());
    const activeVisitor = selectActiveVisitor(getState());

    if (callConversationId) {
      // any media device issues should cause a reject of this thunk
      await startMediaStream();

      setOutgoingVideoEnabled(video);

      if (intentionSource === 'agent' && selfUser && activeVisitor) {
        // Send a chat message to indicate call start to visitor
        const blocks = [makeBlockGeneralEventAgentCall(userToAgent(selfUser))];
        dispatch(
          sendChatMessage(
            makeChatMessageFromAgent(activeVisitor.id, callConversationId, blocks),
          ),
        );
      }

      // starting the media stream can take seconds, if agent dissociates in that time
      // let's flip callConversationId to 0, stopMediaStreamEffect should stop the stream
      if (!selectFindAssociatedRealtimeVisitor(getState())) {
        return 0;
      }
    } else if (
      reason &&
      ['declined', 'missed'].includes(reason) &&
      callConversationId === 0 &&
      intentionSource === 'visitor' &&
      selfUser &&
      activeVisitor
    ) {
      log.debug(`Sending missed call CTA because visitor [${reason}] call`);
      // Send a chat message to indicate call missed to visitor
      const missedCallBlocks = [
        makeBlockGeneralEventVisitorMissedAgentCall(userToAgent(selfUser)),
      ];
      await dispatch(
        sendChatMessage(
          makeChatMessageFromAgent(
            activeVisitor.id,
            callConversationId,
            missedCallBlocks,
          ),
        ),
      );
      // Send a chat message with callback CTA
      const callbackBlocks = [
        makeBlockGeneralEventVisitorCallbackAgent(userToAgent(selfUser)),
      ];
      dispatch(
        sendChatMessage(
          makeChatMessageFromAgent(activeVisitor.id, callConversationId, callbackBlocks),
        ),
      );
    }
    return callConversationId;
  },
);

export const registerImage = createAsyncThunk<ImageRegistration, string>(
  'org/registerImage',
  async filename => {
    const results = await postImage(filename);
    return results.data;
  },
);

export const addHelpCenterArticle = createAsyncThunk<
  HelpCenterArticle,
  {
    title: string;
    description: string;
    content: string;
    userId: number;
    status: HelpCenterArticleStatus;
    articleCollectionId: number | undefined;
    imageIds: string[];
  },
  { state: AppState; rejectValue: RejectError }
>(
  'org/addHelpCenterArticle',
  async ({
    title,
    description,
    content,
    userId,
    status,
    articleCollectionId,
    imageIds,
  }) => {
    return (
      await createHelpCenterArticle(
        title,
        description,
        content,
        userId,
        status,
        articleCollectionId,
        imageIds,
      )
    ).data;
  },
);

export const editHelpCenterArticle = createAsyncThunk<
  HelpCenterArticle,
  Partial<HelpCenterArticle>,
  { state: AppState; rejectValue: RejectError }
>('org/editHelpCenterArticle', async HelpCenterArticle => {
  return (await updateHelpCenterArticle(HelpCenterArticle)).data;
});

export const removeHelpCenterArticle = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeHelpCenterArticle', async id => {
  await deleteHelpCenterArticle(id);
});

export const addHelpCenterCollection = createAsyncThunk<
  HelpCenterCollection,
  {
    title: string;
    color: string;
  },
  { state: AppState; rejectValue: RejectError }
>('org/addHelpCenterCollection', async ({ title, color }) => {
  return (await createHelpCenterCollection(title, color)).data;
});

export const editHelpCenterCollection = createAsyncThunk<
  HelpCenterCollection,
  Partial<HelpCenterCollection>,
  { state: AppState; rejectValue: RejectError }
>('org/editHelpCenterCollection', async HelpCenterCollection => {
  return (await updateHelpCenterCollection(HelpCenterCollection)).data;
});

export const removeHelpCenterCollection = createAsyncThunk<
  void,
  number,
  { state: AppState; rejectValue: RejectError }
>('org/removeHelpCenterCollection', async id => {
  await deleteHelpCenterCollection(id);
});

export const updateHelpCenterCollectionAssignment = createAsyncThunk<
  void,
  {
    oldCollectionId: number;
    newCollectionId: number;
  },
  { state: AppState; rejectValue: RejectError }
>('org/reassignHelpCenterCollection', async data => {
  await reassignHelpCenterCollection(data.oldCollectionId, data.newCollectionId);
});

/**
 * Reusable abstractions
 */

export const addAny = <T>() =>
  createAsyncThunk<
    T,
    { path: string; data: object },
    { state: AppState; rejectValue: RejectError }
  >('org/addAny', async ({ path, data }) => {
    return (await postAny<T>(path, data)).data;
  });

export const editAny = <T>() =>
  createAsyncThunk<
    T,
    { path: string; id: number; data: object },
    { state: AppState; rejectValue: RejectError }
  >('org/editAny', async ({ path, id, data }) => {
    return (await patchAny<T>(path, id, data)).data;
  });

export const reorderAny = createAsyncThunk<
  void,
  { path: string; orderedIds: number[] },
  { state: AppState; rejectValue: RejectError }
>('org/reorderAny', async ({ path, orderedIds }) => {
  return (await putReorderAny(path, orderedIds)).data;
});

export const removeAny = createAsyncThunk<
  void,
  { path: string; id: number },
  { state: AppState; rejectValue: RejectError }
>('org/removeAny', async ({ path, id }) => {
  return (await deleteAny(path, id)).data;
});

// Contact Properties
export const addContactProperty = createAsyncThunk<
  Omit<Property, 'id'>,
  { property: Property },
  { state: AppState; rejectValue: RejectError }
>('org/addContactProperty', async ({ property }, { rejectWithValue }) => {
  try {
    const response = await postContactProperty(property);
    return response.data;
  } catch (error) {
    return rejectWithValue({
      message: error.message || 'Failed to add contact property',
      statusCode: error.response?.status,
    });
  }
});

export const editContactProperty = createAsyncThunk<
  Pick<Property, 'id' | 'displayName' | 'description'>,
  { property: Property },
  { state: AppState; rejectValue: RejectError }
>('org/editontactProperty', async ({ property }) => {
  return (await updateContactProperty(property)).data;
});

// Company Properties
export const addCompanyProperty = createAsyncThunk<
  Omit<Property, 'id'>,
  { property: Property },
  { state: AppState; rejectValue: RejectError }
>('org/addCompanyProperty', async ({ property }, { rejectWithValue }) => {
  try {
    const response = await postCompanyProperty(property);
    return response.data;
  } catch (error) {
    return rejectWithValue({
      message: error.message || 'Failed to add company property',
      statusCode: error.response?.status,
    });
  }
});

export const editCompanyProperty = createAsyncThunk<
  Pick<Property, 'id' | 'displayName'>,
  { property: Property },
  { state: AppState; rejectValue: RejectError }
>('org/editCompanyProperty', async ({ property }) => {
  return (await updateCompanyProperty(property)).data;
});

// Deal Properties
export const addDealProperty = createAsyncThunk<
  Omit<Property, 'id'>,
  { property: Property },
  { state: AppState; rejectValue: RejectError }
>('org/addDealProperty', async ({ property }, { rejectWithValue }) => {
  try {
    const response = await postDealProperty(property);
    return response.data;
  } catch (error) {
    return rejectWithValue({
      message: error.message || 'Failed to add deal property',
      statusCode: error.response?.status,
    });
  }
});

export const editDealProperty = createAsyncThunk<
  Pick<Property, 'id' | 'displayName'>,
  { property: Property },
  { state: AppState; rejectValue: RejectError }
>('org/editDealProperty', async ({ property }) => {
  return (await updateDealProperty(property)).data;
});

export const getUserPermissions = createAsyncThunk<
  PermissionGroup[],
  { userId: number },
  { state: AppState; rejectValue: RejectError }
>('user/getUserPermission', async ({ userId }) => {
  const response = await getUserPermissionsGroup(userId);
  return response.data as PermissionGroup[];
});

export const editUserPermissions = createAsyncThunk<
  PermissionGroup[],
  { userId: number; permissionGroup: PermissionGroup[] },
  { state: AppState; rejectValue: RejectError }
>('user/editUserPermission', async ({ userId, permissionGroup }, { rejectWithValue }) => {
  try {
    const response = await updateUserPermissionsGroup(userId, permissionGroup);
    return response.data;
  } catch (error) {
    return rejectWithValue({
      message: error.message || 'Failed to update user permissions',
      statusCode: error.response?.status,
    });
  }
});

export const refreshActiveVisitor = createAsyncThunk<
  UniqueVisitor | null,
  void,
  { state: AppState }
>('dashboard/refreshActiveVisitor', async (_, { getState }) => {
  const state = getState();
  if (state.dashboard.activeVisitorId) {
    const res = await getUniqueVisitor(state.dashboard.activeVisitorId);
    return res.data;
  }

  return null;
});
